<template>
  <div class="dashboard-container">
    <div class="content">
      <div class="report-container">
        <!-- 顶部标题 + 工具条 -->
        <div class="report-header">
          <div class="header-title">
            <i class="el-icon-money report-icon"></i>
            <span class="report-title">利润表</span>
          </div>
          <div class="header-toolbar">
            <el-radio-group v-model="rangeType" size="mini" @change="handleRangeTypeChange">
              <el-radio-button label="day">按天</el-radio-button>
              <el-radio-button label="month">按月</el-radio-button>
              <el-radio-button label="custom">自定义</el-radio-button>
            </el-radio-group>

            <el-date-picker
                v-if="rangeType === 'day'"
                v-model="queryDate"
                type="date"
                size="mini"
                placeholder="选择日期"
                value-format="yyyy-MM-dd"
                style="width: 140px;"
                @change="loadProfitData">
            </el-date-picker>

            <el-date-picker
                v-else-if="rangeType === 'month'"
                v-model="queryMonth"
                type="month"
                size="mini"
                placeholder="选择月份"
                value-format="yyyy-MM"
                style="width: 140px;"
                @change="loadProfitData">
            </el-date-picker>

            <template v-else>
              <el-date-picker
                  v-model="customRange[0]"
                  type="datetime"
                  size="mini"
                  placeholder="开始时间"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  style="width: 200px;">
              </el-date-picker>
              <span style="margin: 0 6px;">至</span>
              <el-date-picker
                  v-model="customRange[1]"
                  type="datetime"
                  size="mini"
                  placeholder="结束时间"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  style="width: 200px;">
              </el-date-picker>
            </template>

            <el-button type="primary" size="mini" icon="el-icon-search" @click="loadProfitData">查询</el-button>
            <el-button size="mini" icon="el-icon-refresh" @click="resetQuery">重置</el-button>
          </div>
        </div>

        <div class="report-body">
          <!-- 5 张指标卡 -->
          <div class="profit-cards-container">
            <div class="metric-card income-card">
              <div class="metric-label">总收入</div>
              <div class="metric-value" :class="profitData.allIncomeAmount >= 0 ? 'income-value' : 'profit-negative'">
                {{ formatMoney(profitData.allIncomeAmount) }}
              </div>
              <div class="metric-sub">本期收入合计</div>
            </div>
            <div class="metric-card cost-card">
              <div class="metric-label">医疗成本</div>
              <div class="metric-value" :class="profitData.allMedicineCost >= 0 ? 'cost-value' : 'profit-negative'">
                {{ formatMoney(profitData.allMedicineCost) }}
              </div>
              <div class="metric-sub">本期医疗成本</div>
            </div>
            <div class="metric-card operation-card">
              <div class="metric-label">运营费用</div>
              <div class="metric-value" :class="profitData.allOperationAmount >= 0 ? 'operation-value' : 'profit-negative'">
                {{ formatMoney(profitData.allOperationAmount) }}
              </div>
              <div class="metric-sub">本期运营费用</div>
            </div>
            <div class="metric-card profit-card">
              <div class="metric-label">净利润</div>
              <div class="metric-value" :class="profitData.allProfitAmount >= 0 ? 'profit-positive' : 'profit-negative'">
                {{ formatMoney(profitData.allProfitAmount) }}
              </div>
              <div class="metric-sub">盈利</div>
            </div>
            <div class="metric-card rate-card">
              <div class="metric-label">毛利率</div>
              <div class="metric-value" :class="getPercentValue(profitData.profitMargin) >= 0 ? 'rate-value' : 'profit-negative'">
                {{ formatPercent(profitData.profitMargin) }}
              </div>
              <div class="metric-sub">毛利 / 收入</div>
            </div>
          </div>

          <!-- 利润趋势图 -->
          <div class="chart-card">
            <div class="chart-header">
              <div>
                <div class="chart-title">利润趋势</div>
                <div class="chart-sub">统计区间：{{ currentRangeLabel }}</div>
              </div>
            </div>
            <div ref="trendChart" class="chart-body"></div>
          </div>

          <!-- 利润表明细 -->
          <div class="detail-card">
            <div class="detail-header">
              <div>
                <div class="detail-title">利润表明细</div>
                <div class="detail-sub">统计区间：{{ currentRangeLabel }}</div>
              </div>
              <el-button size="mini" icon="el-icon-download" @click="exportExcel">导出Excel</el-button>
            </div>
            <el-table
                :data="detailTableData"
                border
                row-key="id"
                :tree-props="{children: 'children'}"
                default-expand-all
                :row-class-name="detailRowClassName"
                style="width: 100%;"
                :header-cell-style="headerCellStyle">
              <el-table-column prop="name" label="项目" min-width="300">
                <template slot-scope="scope">
                  <span :style="{ paddingLeft: (scope.row.level || 0) * 16 + 'px' }">
                    {{ scope.row.name }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="amount" label="金额（元）" width="200" align="right">
                <template slot-scope="scope">
                  <span :class="scope.row.amountClass">
                    <template v-if="scope.row.isPercent">
                      {{ formatPercent(scope.row.amount) }}
                    </template>
                    <template v-else>
                      {{ formatMoney(scope.row.amount) }}
                    </template>
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { getProfitReport } from '@/api/statisticalReport/statisticalReport';

export default {
  name: 'FinancialIncomeStatistics',
  data() {
    return {
      rangeType: 'month',
      queryDate: dayjs().format('YYYY-MM-DD'),
      queryMonth: dayjs().format('YYYY-MM'),
      customRange: [
        dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss'),
      ],

      profitData: {},
      monthReportVoList: [],
      chartInstance: null,
    };
  },
  computed: {
    // 三种模式统一输出带时分秒的时间范围
    queryParams() {
      if (this.rangeType === 'day') {
        return {
          startDate: `${this.queryDate} 00:00:00`,
          endDate: `${this.queryDate} 23:59:59`,
        };
      }
      if (this.rangeType === 'month') {
        return {
          startDate: dayjs(this.queryMonth).startOf('month').format('YYYY-MM-DD HH:mm:ss'),
          endDate: dayjs(this.queryMonth).endOf('month').format('YYYY-MM-DD HH:mm:ss'),
        };
      }
      return {
        startDate: this.customRange[0],
        endDate: this.customRange[1],
      };
    },
    currentRangeLabel() {
      if (this.rangeType === 'day') {
        return `${this.queryDate} 00:00:00 ~ ${this.queryDate} 23:59:59`;
      }
      if (this.rangeType === 'month') {
        return `${dayjs(this.queryMonth).startOf('month').format('YYYY-MM-DD HH:mm:ss')} ~ ${dayjs(this.queryMonth).endOf('month').format('YYYY-MM-DD HH:mm:ss')}`;
      }
      return `${this.customRange[0]} ~ ${this.customRange[1]}`;
    },
    monthList() {
      return (this.monthReportVoList || []).map(item => ({
        label: item.yearMonth,
        income: toNumber(item.allIncomeAmount),
        medicineCost: toNumber(item.allMedicineCost),
        operation: toNumber(item.allOperationAmount),
        profit: toNumber(item.allProfitAmount),
      }));
    },
    // 运营费用明细子项（由 operationAmountList 动态生成，支持多条循环）
    operationItems() {
      const list = (this.profitData && this.profitData.operationAmountList) || [];
      return list.map((item, index) => ({
        id: `op-item-${index}`,
        name: item.className || `运营费用项${index + 1}`,
        amount: toNumber(item.amount),
        level: 1,
        amountClass: getAmountClass(item.amount),
      }));
    },
    detailTableData() {
      const d = this.profitData || {};

      // 运营费用子项：动态 + 合计
      const operationChildren = [
        ...this.operationItems,
        {
          id: 'op-total',
          name: '运营费用合计',
          amount: toNumber(d.allOperationAmount),
          rowType: 'total',
          level: 1,
          amountClass: getAmountClass(d.allOperationAmount),
        },
      ];

      return [
        // ===== 一、收入类 =====
        {
          id: 'income',
          name: '一、收入类',
          amount: null,
          rowType: 'section',
          level: 0,
          children: [
            { id: 'reg-income', name: '挂号收入', amount: toNumber(d.registrationIncome), level: 1 },
            { id: 'drug-income', name: '西药收入', amount: toNumber(d.drugIncome), level: 1 },
            { id: 'tcm-drug-income', name: '中药收入', amount: toNumber(d.tcmDrugIncome), level: 1 },
            { id: 'eisai-income', name: '卫材收入', amount: toNumber(d.eisaiIncome), level: 1 },
            { id: 'check-income', name: '检查收入', amount: toNumber(d.checkIncome), level: 1 },
            { id: 'treat-income', name: '治疗收入', amount: toNumber(d.treatIncome), level: 1 },
            { id: 'other-med-income', name: '其他医疗收入', amount: toNumber(d.otherMedicineIncome), level: 1 },
            { id: 'other-income', name: '其他收入', amount: toNumber(d.otherIncome), level: 1 },
            {
              id: 'income-total',
              name: '收入合计',
              amount: toNumber(d.allIncomeAmount),
              rowType: 'total',
              level: 1,
              amountClass: getAmountClass(d.allIncomeAmount),
            },
          ],
        },

        // ===== 二、医疗成本类 =====
        {
          id: 'cost',
          name: '二、医疗成本类',
          amount: null,
          rowType: 'section',
          level: 0,
          children: [
            { id: 'drug-cost', name: '西药成本', amount: toNumber(d.drugCost), level: 1 },
            { id: 'tcm-drug-cost', name: '中药成本', amount: toNumber(d.tcmDrugCost), level: 1 },
            { id: 'eisai-cost', name: '卫材成本', amount: toNumber(d.eisaiCost), level: 1 },
            { id: 'consumable-cost', name: '耗材费', amount: toNumber(d.consumableCost), level: 1 },
            {
              id: 'cost-total',
              name: '医疗成本合计',
              amount: toNumber(d.allMedicineCost),
              rowType: 'total',
              level: 1,
              amountClass: getAmountClass(d.allMedicineCost),
            },
          ],
        },

        // ===== 三、运营费用类 =====
        {
          id: 'operation',
          name: '三、运营费用类',
          amount: null,
          rowType: 'section',
          level: 0,
          children: operationChildren,
        },

        // ===== 四、净利润 =====
        {
          id: 'net-profit',
          name: '四、净利润',
          amount: null,
          rowType: 'section',
          level: 0,
          children: [
            {
              id: 'net-profit-item',
              name: '净利润',
              amount: toNumber(d.allProfitAmount),
              level: 1,
              amountClass: getAmountClass(d.allProfitAmount),
            },
            {
              id: 'profit-margin',
              name: '毛利率',
              amount: d.profitMargin,
              level: 1,
              isPercent: true,
            },
          ],
        },
      ];
    },
  },
  mounted() {
    this.loadProfitData();
    window.addEventListener('resize', this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart);
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  methods: {
    async loadProfitData() {
      // 自定义模式下做时间范围校验
      if (this.rangeType === 'custom') {
        const [s, e] = this.customRange;
        if (!s || !e) {
          this.$message.warning('请选择完整的时间范围');
          return;
        }
        if (dayjs(s).isAfter(dayjs(e))) {
          this.$message.warning('开始时间不能晚于结束时间');
          return;
        }
      }

      try {
        const res = await getProfitReport(this.queryParams);
        if (res.code === 0 || res.code === 200) {
          const data = res.data || {};
          // 兼容后端可能返回负数成本，这里归零
          if (toNumber(data.allMedicineCost) < 0) {
            data.allMedicineCost = 0;
          }
          this.profitData = data;
          this.monthReportVoList = data.monthReportVoList || [];
          this.$nextTick(() => this.renderTrendChart());
        } else {
          this.$message.error(res.message || '获取利润数据失败');
        }
      } catch (e) {
        console.error(e);
        this.$message.error('获取利润数据失败');
      }
    },

    resetQuery() {
      this.rangeType = 'month';
      this.queryMonth = dayjs().format('YYYY-MM');
      this.queryDate = dayjs().format('YYYY-MM-DD');
      this.customRange = [
        dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss'),
      ];
      this.loadProfitData();
    },

    handleRangeTypeChange() {
      this.loadProfitData();
    },

    renderTrendChart() {
      const el = this.$refs.trendChart;
      if (!el) return;
      if (!this.chartInstance) this.chartInstance = echarts.init(el);

      const list = this.monthList;
      const xData = list.map(i => i.label);

      this.chartInstance.setOption(
          {
            tooltip: {
              trigger: 'axis',
              valueFormatter: v => '¥' + Number(v || 0).toFixed(2),
            },
            legend: {
              data: ['收入', '医疗成本', '运营费用', '净利润'],
              right: 20,
              top: 0,
            },
            grid: { left: 70, right: 30, top: 40, bottom: 30 },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: xData,
              axisLine: { lineStyle: { color: '#dcdfe6' } },
              axisLabel: { color: '#909399' },
            },
            yAxis: {
              type: 'value',
              scale: true,
              axisLine: { show: false },
              axisTick: { show: false },
              splitLine: { lineStyle: { color: '#f0f0f0' } },
              axisLabel: { color: '#909399', formatter: '¥{value}' },
            },
            series: [
              {
                name: '收入', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6,
                itemStyle: { color: '#10b981' }, lineStyle: { color: '#10b981', width: 2 },
                data: list.map(i => i.income),
              },
              {
                name: '医疗成本', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6,
                itemStyle: { color: '#94a3b8' }, lineStyle: { color: '#94a3b8', width: 2 },
                data: list.map(i => i.medicineCost),
              },
              {
                name: '运营费用', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6,
                itemStyle: { color: '#f59e0b' }, lineStyle: { color: '#f59e0b', width: 2 },
                data: list.map(i => i.operation),
              },
              {
                name: '净利润', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6,
                itemStyle: { color: '#6366f1' }, lineStyle: { color: '#6366f1', width: 2 },
                data: list.map(i => i.profit),
              },
            ],
          },
          true,
      );
    },

    resizeChart() {
      if (this.chartInstance) this.chartInstance.resize();
    },

    // 金额：负号提前，例如 -¥1234.56
    formatMoney(v) {
      if (v === null || v === undefined || v === '') return '¥0.00';
      const n = toNumber(v);
      return (n < 0 ? '-' : '') + '¥' + Math.abs(n).toFixed(2);
    },

    // 百分比：保留两位
    formatPercent(v) {
      if (v === null || v === undefined || v === '') return '0.00%';
      const n = parseFloat(String(v).replace('%', ''));
      return (isNaN(n) ? 0 : n).toFixed(2) + '%';
    },

    // 从 "54.98%" 里取数值，用于颜色判断
    getPercentValue(v) {
      if (!v) return 0;
      const n = parseFloat(String(v).replace('%', ''));
      return isNaN(n) ? 0 : n;
    },

    headerCellStyle() {
      return {
        background: '#fafafa',
        color: '#606266',
        fontWeight: 'bold',
        fontSize: '13px',
        padding: '10px 6px',
      };
    },

    detailRowClassName({ row }) {
      if (row.rowType === 'section') return 'detail-section-row';
      if (row.rowType === 'total') return 'detail-total-row';
      return '';
    },

    exportExcel() {
      this.$message.info('导出 Excel（待接入后端导出接口）');
    },
  },
};

// ===== 工具方法 =====

// 安全转数字：处理 0E-8、null、undefined、字符串等
function toNumber(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ===== 明细表行颜色：正数绿、负数红 =====
function getAmountClass(v) {
  if (v === null || v === undefined || v === '') return '';
  return toNumber(v) >= 0 ? 'profit-positive' : 'profit-negative';
}
</script>

<style scoped lang="less">
.dashboard-container {
  display: flex;
  height: 100%;
  background: #f5f7fa;
}

.content {
  flex: 1;
  overflow: auto;
  background: #f5f7fa;
}

.report-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px;
  background: white;
  border-bottom: 1px solid #ebeef5;
}

.header-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.report-icon {
  font-size: 22px;
  margin-right: 10px;
  color: #409eff;
}

.header-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.report-body {
  flex: 1;
  overflow: auto;
  padding: 16px 20px 24px;
  background: #f5f7fa;
}

/* ===== 指标卡 ===== */
.profit-cards-container {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.metric-card {
  flex: 1;
  min-width: 0;
  border-radius: 8px;
  background: #fff;
  padding: 16px 18px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s;
}

.metric-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.metric-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.metric-value {
  font-size: 22px;
  font-weight: bold;
  line-height: 1.2;
  margin-bottom: 6px;
}

.metric-sub {
  font-size: 12px;
  color: #c0c4cc;
}

.income-card { border-left: 3px solid #10b981; }
.income-value { color: #10b981; }
.cost-card { border-left: 3px solid #606266; }
.cost-value { color: #303133; }
.operation-card { border-left: 3px solid #f59e0b; }
.operation-value { color: #f59e0b; }
.profit-card { border-left: 3px solid #409eff; }
.rate-card { border-left: 3px solid #10b981; }
.rate-value { color: #10b981; }

.profit-positive { color: #10b981; }
.profit-negative { color: #f56c6c; }

/* ===== 图表卡片 ===== */
.chart-card {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px 20px;
}

.chart-header {
  margin-bottom: 8px;
}

.chart-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.chart-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.chart-body {
  width: 100%;
  height: 320px;
}

/* ===== 明细表格 ===== */
.detail-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.detail-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.detail-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.detail-card /deep/ .detail-section-row td {
  background: #f5f7fa !important;
  font-weight: bold;
  color: #303133;
}

.detail-card /deep/ .detail-total-row td {
  background: #ecf5ff !important;
  font-weight: bold;
  color: #409eff;
}

.detail-card /deep/ .el-table__row td {
  padding: 8px 6px;
}
</style>